import type { KarlAsk } from "../karlAsks";
import { orderKarlAsks } from "./orderKarlAsks";
import type { KarlAskQueueMode, KarlAskQueueView, ReadAskQueue } from "./types";

export const readAskQueue: ReadAskQueue = ({ asks, state }) => {
  const { queue, stored } = orderKarlAsks({ asks, state });
  // What he already said, in this walk or before it: a walk that starts over must not read "4 of 21".
  const answeredInWalk = state.history.length;
  const behind = answeredInWalk + stored.length;
  const total = behind + queue.length;
  const ahead = Math.max(0, queue.length - 1);
  const progress = total ? behind / total : 0;
  const putOff = new Set(state.deferred);

  // The caption, not `subjectKey`: a board's asks share its name while their keys are prefixed per ask ("fedFrom:V1").
  const subjectOf = (ask: KarlAsk): string | null => ask.subject ?? null;
  const answered = state.history
    .map((record) => asks.find((ask) => ask.id === record.askId))
    .filter((ask): ask is KarlAsk => Boolean(ask));
  const subjects = [
    ...new Set(
      [...stored, ...answered, ...queue].map(subjectOf).filter((subject): subject is string => subject !== null),
    ),
  ];
  const subjectPositionOf = (ask: KarlAsk | null): number => {
    const subject = ask ? subjectOf(ask) : null;
    return subject === null ? 0 : subjects.indexOf(subject) + 1;
  };

  // A rewind past the start of history would read a negative index.
  const rewind = Math.min(Math.max(0, state.rewind), answeredInWalk);
  const canGoBack = answeredInWalk > rewind;
  const canGoForward = rewind > 0;

  const view = (
    ask: KarlAsk | null,
    mode: KarlAskQueueMode,
    position: number,
  ): KarlAskQueueView => ({
    ask,
    mode,
    arm: mode === "armed" && state.armed ? state.armed.arm : null,
    behind,
    ahead,
    total,
    position,
    subjectPosition: subjectPositionOf(ask),
    subjectTotal: subjects.length,
    progress,
    canGoBack,
    canGoForward,
    isDeferred: Boolean(ask && putOff.has(ask.id)),
    isToolArm: mode === "armed" && Boolean(state.armed?.isTool),
  });

  if (rewind > 0) {
    const record = state.history[answeredInWalk - rewind];
    const revisited = asks.find((ask) => ask.id === record.askId) ?? null;
    return view(revisited, "revisiting", stored.length + answeredInWalk - rewind + 1);
  }

  const ask = queue[0] ?? null;
  if (state.armed) return view(ask, "armed", ask ? behind + 1 : 0);
  if (!ask) return view(null, "finished", 0);
  return view(ask, "asking", behind + 1);
};
